import { ClipboardPlus, Pencil, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';

import { SectionCard } from './SectionCard';

const DIAGNOSIS_KINDS = ['presumptive', 'differential', 'confirmed', 'rule_out'] as const;

export interface DiagnosisRow {
  id: string;
  diagnosis_kind: string;
  description: string;
  is_primary: boolean;
}

export interface NewDiagnosisDraft {
  kind: string;
  description: string;
  is_primary: boolean;
}

export interface DiagnosisUpdatePatch {
  diagnosis_kind?: string;
  description?: string;
  is_primary?: boolean;
}

interface DiagnosesSectionProps {
  diagnoses: DiagnosisRow[];
  can_diag: boolean;
  editable: boolean;
  new_diag: NewDiagnosisDraft;
  on_change_new_diag: (patch: Partial<NewDiagnosisDraft>) => void;
  on_add: () => void;
  on_remove: (id: string) => void;
  on_update: (id: string, patch: DiagnosisUpdatePatch) => void;
}

function DiagnosisCard({
  diagnosis,
  can_edit,
  on_remove,
  on_update,
}: {
  diagnosis: DiagnosisRow;
  can_edit: boolean;
  on_remove: () => void;
  on_update: (patch: DiagnosisUpdatePatch) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<DiagnosisUpdatePatch>({
    diagnosis_kind: diagnosis.diagnosis_kind,
    description: diagnosis.description,
    is_primary: diagnosis.is_primary,
  });

  if (editing) {
    return (
      <li className="space-y-2 rounded-md border p-3">
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="border-input bg-background rounded-md border px-2 py-1 text-sm"
            value={draft.diagnosis_kind}
            onChange={(e) => setDraft({ ...draft, diagnosis_kind: e.target.value })}
          >
            {DIAGNOSIS_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`clinical.diagnosis_kind_${k}`)}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              checked={draft.is_primary ?? false}
              onChange={(e) => setDraft({ ...draft, is_primary: e.target.checked })}
            />
            {t('clinical.is_primary')}
          </label>
        </div>
        <Input
          value={draft.description ?? ''}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        />
        <div className="flex justify-end gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              on_update(draft);
              setEditing(false);
            }}
          >
            {t('common.save')}
          </Button>
        </div>
      </li>
    );
  }

  return (
    <li className="flex items-center justify-between gap-2 rounded-md border px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">{t(`clinical.diagnosis_kind_${diagnosis.diagnosis_kind}`)}</Badge>
          {diagnosis.is_primary ? (
            <Badge variant="default">
              <Star className="mr-1 h-3 w-3" aria-hidden />
              {t('clinical.is_primary')}
            </Badge>
          ) : null}
        </div>
        <p className="mt-1 text-sm">{diagnosis.description}</p>
      </div>
      {can_edit ? (
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            aria-label={t('common.edit')}
            onClick={() => setEditing(true)}
          >
            <Pencil className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            aria-label={t('clinical.delete')}
            onClick={on_remove}
          >
            <Trash2 className="text-destructive h-4 w-4" aria-hidden />
          </Button>
        </div>
      ) : null}
    </li>
  );
}

/** Diagnósticos as cards/chips (with edit) before the add-form — same
 *  add/remove/update mutations as before, only the presentation changed. */
export function DiagnosesSection({
  diagnoses,
  can_diag,
  editable,
  new_diag,
  on_change_new_diag,
  on_add,
  on_remove,
  on_update,
}: DiagnosesSectionProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="space-y-3">
      <SectionCard icon={ClipboardPlus} title={t('clinical.tab_diagnoses')}>
        {diagnoses.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('clinical.no_diagnoses')}</p>
        ) : (
          <ul className="space-y-2">
            {diagnoses.map((d) => (
              <DiagnosisCard
                key={d.id}
                diagnosis={d}
                can_edit={editable && can_diag}
                on_remove={() => on_remove(d.id)}
                on_update={(patch) => on_update(d.id, patch)}
              />
            ))}
          </ul>
        )}
      </SectionCard>

      {editable && can_diag ? (
        <SectionCard icon={ClipboardPlus} title={t('clinical.add_diagnosis')}>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="border-input bg-background rounded-md border px-2 py-1 text-sm"
              value={new_diag.kind}
              onChange={(e) => on_change_new_diag({ kind: e.target.value })}
            >
              {DIAGNOSIS_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`clinical.diagnosis_kind_${k}`)}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={new_diag.is_primary}
                onChange={(e) => on_change_new_diag({ is_primary: e.target.checked })}
              />
              {t('clinical.is_primary')}
            </label>
          </div>
          <Input
            placeholder={t('clinical.diagnosis_description')}
            value={new_diag.description}
            onChange={(e) => on_change_new_diag({ description: e.target.value })}
          />
          <Button type="button" size="sm" onClick={on_add}>
            {t('clinical.add_diagnosis')}
          </Button>
        </SectionCard>
      ) : null}
    </div>
  );
}
